/**
 * Forecast sensitivity — ±% levers on CAC, marketing budget, and churn.
 * Pure functions; does not mutate the input pack.
 */

import { PAID_BUDGET_MODE_CUSTOM } from './channels';
import {
  computeCashRecoveryMonths,
  computeMonthsToProfitFromBand,
} from './projectForecast';
import { scaleRetentionChurn } from './cohorts';

function clonePack(pack) {
  return JSON.parse(JSON.stringify(pack));
}

/**
 * Clone pack and apply percentage shocks to CAC, marketing, and/or churn.
 * @param {object} pack
 * @param {{ cacPct?: number, marketingPct?: number, churnPct?: number }} shocks — e.g. +20 or -20
 */
export function applySensitivity(pack, { cacPct = 0, marketingPct = 0, churnPct = 0 } = {}) {
  const next = clonePack(pack);

  if (cacPct !== 0 && next.channels?.paid) {
    const multiplier = Math.max(0, 1 + cacPct / 100);
    next.channels.paid.cac = Math.max(
      1,
      (next.channels.paid.cac || 1) * multiplier
    );
  }

  if (marketingPct !== 0 && next.assumptions) {
    const mult = Math.max(0, 1 + marketingPct / 100);
    next.assumptions.marketingBudgetMo =
      (next.assumptions.marketingBudgetMo || 0) * mult;
    if (next.assumptions.marketingPhases?.length) {
      next.assumptions.marketingPhases = next.assumptions.marketingPhases.map((p) => ({
        ...p,
        budgetMo: (p.budgetMo || 0) * mult,
      }));
    }
    if (next.channels?.paid) {
      const mode = next.channels.paid.budgetMode;
      if (mode === PAID_BUDGET_MODE_CUSTOM) {
        // Custom: scale acquisition budget separately (cash already scaled above).
        next.channels.paid.budgetMo = (next.channels.paid.budgetMo || 0) * mult;
      } else {
        // Link (default): keep paid.budgetMo mirrored to marketing cash (phase 0 fallback).
        next.channels.paid.budgetMo = next.assumptions.marketingBudgetMo;
        next.channels.paid.budgetMode = next.channels.paid.budgetMode || 'link';
      }
    }
  }

  if (churnPct !== 0 && next.cohorts?.retention?.length) {
    const multiplier = Math.max(0, 1 + churnPct / 100);
    next.cohorts.retention = scaleRetentionChurn(next.cohorts.retention, multiplier);
  }

  return next;
}

function horizonMrrForBand(projection, band) {
  const last = projection.months[projection.months.length - 1];
  if (!last) return 0;
  if (projection.mc) {
    const key = `mrrP${band.slice(1)}`;
    return last[key] ?? last.endMrr ?? 0;
  }
  return last.endMrr ?? 0;
}

function cashPathForBand(projection, band) {
  return [
    projection.openingCash ?? projection.months?.[0]?.cash,
    ...(projection.months || []).map((month) => {
      if (band === 'p10') return month.cashP10 ?? month.cash;
      if (band === 'p90') return month.cashP90 ?? month.cash;
      return month.cashP50 ?? month.cash;
    }),
  ];
}

function runwayForBand(projection, band) {
  if (band === 'p10') return projection.runwayP10 ?? projection.runwayMonths;
  if (band === 'p90') return projection.runwayP90 ?? projection.runwayMonths;
  return projection.runwayMonths;
}

/**
 * Compare baseline vs stressed projection at a band.
 * @param {object} baselineProjection
 * @param {object} stressedProjection
 * @param {'p10'|'p50'|'p90'} [band='p50']
 */
export function compareSensitivity(baselineProjection, stressedProjection, band = 'p50') {
  const baselineHorizonMrr = horizonMrrForBand(baselineProjection, band);
  const stressedHorizonMrr = horizonMrrForBand(stressedProjection, band);
  const baselineMtp = computeMonthsToProfitFromBand(baselineProjection.months, band);
  const stressedMtp = computeMonthsToProfitFromBand(stressedProjection.months, band);
  const baselineRecovery = computeCashRecoveryMonths(
    cashPathForBand(baselineProjection, band)
  );
  const stressedRecovery = computeCashRecoveryMonths(
    cashPathForBand(stressedProjection, band)
  );
  const baselineRunway = runwayForBand(baselineProjection, band);
  const stressedRunway = runwayForBand(stressedProjection, band);

  return {
    band,
    deltaHorizonMrr: stressedHorizonMrr - baselineHorizonMrr,
    deltaRunway: (stressedRunway ?? 0) - (baselineRunway ?? 0),
    deltaMonthsToProfit:
      baselineMtp == null || stressedMtp == null ? null : stressedMtp - baselineMtp,
    deltaCashRecovery:
      baselineRecovery == null || stressedRecovery == null
        ? null
        : stressedRecovery - baselineRecovery,
    baseline: {
      horizonMrr: baselineHorizonMrr,
      runway: baselineRunway,
      monthsToProfit: baselineMtp,
      cashRecovery: baselineRecovery,
    },
    stressed: {
      horizonMrr: stressedHorizonMrr,
      runway: stressedRunway,
      monthsToProfit: stressedMtp,
      cashRecovery: stressedRecovery,
    },
  };
}
